import { TechRuleFinding } from 'store/api/apiTypes';

export type TechnicalAccessibilityRuleType = 'llms' | 'schema';

export const TECHNICAL_ACCESSIBILITY_UNABLE_TO_CHECK_VALUE =
  'unable_to_check' as const;

export const TECHNICAL_ACCESSIBILITY_RULE_KEYS = {
  llms: ['file', 'structure', 'products'],
  schema: ['schema', 'organization_schema', 'website_schema'],
} as const;

export const TECHNICAL_ACCESSIBILITY_TOTAL_RULES =
  1 +
  TECHNICAL_ACCESSIBILITY_RULE_KEYS.llms.length +
  TECHNICAL_ACCESSIBILITY_RULE_KEYS.schema.length;

const TECHNICAL_ACCESSIBILITY_FALLBACK_MAX_SCORES: Record<string, number> = {
  file: 4,
  structure: 3,
  products: 3,
  schema: 4,
  organization_schema: 3,
  website_schema: 3,
};

type ResponseWithStatusCode = {
  statusCode?: number;
  rule?: string;
  score?: number | null;
  findings?: unknown;
};

const isUnableToCheckValue = (
  value: unknown
): value is typeof TECHNICAL_ACCESSIBILITY_UNABLE_TO_CHECK_VALUE =>
  value === TECHNICAL_ACCESSIBILITY_UNABLE_TO_CHECK_VALUE;

const hasFiniteTechnicalScore = (score: unknown): score is number =>
  typeof score === 'number' && Number.isFinite(score);

const hasUnableToCheckFinding = (findings: unknown): boolean => {
  if (!findings || typeof findings !== 'object' || Array.isArray(findings)) {
    return false;
  }

  return Object.values(findings as Record<string, unknown>).some(finding => {
    if (!finding || typeof finding !== 'object' || Array.isArray(finding)) {
      return false;
    }

    return isUnableToCheckValue((finding as { exists?: unknown }).exists);
  });
};

const createMissingFinding = (key: string): TechRuleFinding => ({
  score: 0,
  max_score: TECHNICAL_ACCESSIBILITY_FALLBACK_MAX_SCORES[key] ?? 0,
});

export const hasTechnicalRuleData = (
  data?: ResponseWithStatusCode | null
): boolean => {
  if (!data || data.statusCode === 202) {
    return false;
  }

  return (
    (typeof data.statusCode !== 'number' ||
      (data.statusCode >= 200 && data.statusCode < 300)) &&
    Boolean(data.rule) &&
    (hasFiniteTechnicalScore(data.score) ||
      hasUnableToCheckFinding(data.findings))
  );
};

export const normalizeTechnicalRuleFindings = (
  findings: Record<string, TechRuleFinding> | undefined,
  ruleType: TechnicalAccessibilityRuleType
): Record<string, TechRuleFinding> =>
  Object.fromEntries(
    TECHNICAL_ACCESSIBILITY_RULE_KEYS[ruleType].map(key => [
      key,
      findings?.[key] ?? createMissingFinding(key),
    ])
  );
